import { useState, useEffect, useCallback } from "react";
import { User, Radio, Wifi, WifiOff, Loader2, AlertTriangle, Palette, Check, Target, Plus, X } from "lucide-react";
import { ProfileSettingsForm, PasswordChangeForm } from "./ProfileForms";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DASHBOARD_THEMES = {
  classic: { label: "Classic", desc: "Traditional radio, warm navy tones", preview: ["#020617", "#0f172a", "#3b82f6"] },
  modern: { label: "Modern", desc: "Vibrant purple, the default look", preview: ["#000000", "#030712", "#a855f7"] },
  dark: { label: "Dark", desc: "Pure dark studio with emerald accents", preview: ["#000000", "#0a0a0a", "#10b981"] },
  minimal: { label: "Minimal", desc: "Clean and muted, distraction-free", preview: ["#09090b", "#18181b", "#a1a1aa"] },
  premium: { label: "Premium", desc: "Luxury gold finish", preview: ["#0c0a09", "#1c1917", "#f59e0b"] },
};

const ThemePicker = ({ currentTheme, onThemeChange }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="theme-picker-section">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
        <Palette className="w-5 h-5 text-white/70" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-white">Dashboard Theme</h3>
        <p className="text-white/40 text-xs">Choose your dashboard look and feel</p>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {Object.entries(DASHBOARD_THEMES).map(([key, theme]) => {
        const isActive = currentTheme === key;
        return (
          <button
            key={key}
            onClick={() => onThemeChange(key)}
            className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 ${
              isActive ? "border-white/40 bg-white/10" : "border-white/5 bg-white/[0.02] hover:bg-white/5 hover:border-white/10"
            }`}
            data-testid={`theme-${key}`}
          >
            {isActive && (
              <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: theme.preview[2] }}>
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <div className="flex items-center gap-2 mb-2.5">
              {theme.preview.map((c, i) => (
                <div key={i} className="w-5 h-5 rounded-full border border-white/10" style={{ background: c }} />
              ))}
            </div>
            <p className="text-white text-sm font-semibold">{theme.label}</p>
            <p className="text-white/40 text-[11px] mt-0.5">{theme.desc}</p>
          </button>
        );
      })}
    </div>
  </div>
);

const ExternalStreamSection = ({ authToken }) => {
  const [settings, setSettings] = useState({ server_type: "icecast", server_url: "", server_port: 8000, mount_point: "/stream", stream_id: 1 });
  const [saved, setSaved] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/stream/external/settings`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.server_url) { setSettings(data); setHasSaved(true); }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!hasSaved) return;
    const poll = () => {
      fetch(`${BACKEND_URL}/api/stream/external/status`, { headers })
        .then(r => r.json())
        .then(setLiveStatus)
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
  }, [hasSaved]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stream/external/settings`, { method: "PUT", headers, body: JSON.stringify(settings) });
      const data = await res.json();
      if (data.status === "success") { setSaved(true); setHasSaved(true); setTimeout(() => setSaved(false), 3000); }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stream/external/test`, { method: "POST", headers, body: JSON.stringify(settings) });
      setTestResult(await res.json());
    } catch (e) { setTestResult({ online: false, error: "Request failed" }); }
    finally { setTesting(false); }
  };

  const handleRemove = async () => {
    await fetch(`${BACKEND_URL}/api/stream/external/settings`, { method: "DELETE", headers });
    setSettings({ server_type: "icecast", server_url: "", server_port: 8000, mount_point: "/stream", stream_id: 1 });
    setHasSaved(false);
    setLiveStatus(null);
    setTestResult(null);
  };

  if (loading) return null;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="external-stream-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center"><Radio className="w-5 h-5 text-purple-400" /></div>
          <div>
            <h3 className="text-lg font-semibold text-white">External Stream</h3>
            <p className="text-white/40 text-xs">Connect your Shoutcast or Icecast server</p>
          </div>
        </div>
        {liveStatus?.online && (
          <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 px-3 py-1.5 rounded-full">
            <Wifi className="w-3.5 h-3.5 text-green-400" />
            <span className="text-green-400 text-xs font-medium">{liveStatus.listeners} listeners</span>
          </div>
        )}
      </div>

      {/* Live Now Playing */}
      {liveStatus?.online && liveStatus?.now_playing && (
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-5 flex items-center gap-4" data-testid="external-now-playing">
          <div className="flex items-end gap-0.5 h-6 flex-shrink-0">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-1 bg-green-400 rounded-full animate-pulse" style={{ height: `${10 + Math.random() * 14}px`, animationDelay: `${i * 120}ms` }} />
            ))}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{liveStatus.now_playing.title || "Unknown"}</p>
            <p className="text-white/40 text-xs truncate">{liveStatus.now_playing.artist || ""} {liveStatus.stream_name ? `· ${liveStatus.stream_name}` : ""}</p>
          </div>
        </div>
      )}

      {/* Server Type */}
      <div className="mb-4">
        <label className="block text-white/50 text-xs mb-1.5">Server Type</label>
        <div className="grid grid-cols-2 gap-2">
          {["icecast", "shoutcast"].map(t => (
            <button key={t} type="button" onClick={() => setSettings({ ...settings, server_type: t })}
              className={`py-2.5 rounded-xl text-sm font-medium transition capitalize ${settings.server_type === t ? "bg-purple-500 text-white" : "bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"}`}
              data-testid={`stream-type-${t}`}>{t}</button>
          ))}
        </div>
      </div>

      {/* URL + Port */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="col-span-2">
          <label className="block text-white/50 text-xs mb-1.5">Server URL</label>
          <input type="text" value={settings.server_url} onChange={(e) => setSettings({ ...settings, server_url: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
            placeholder="http://your-server.com" data-testid="stream-url-input" />
        </div>
        <div>
          <label className="block text-white/50 text-xs mb-1.5">Port</label>
          <input type="number" value={settings.server_port} onChange={(e) => setSettings({ ...settings, server_port: parseInt(e.target.value) || 0 })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
            placeholder="8000" data-testid="stream-port-input" />
        </div>
      </div>

      {/* Mount / SID */}
      <div className="mb-5">
        <label className="block text-white/50 text-xs mb-1.5">
          {settings.server_type === "icecast" ? "Mount Point" : "Stream ID (SID)"}
        </label>
        {settings.server_type === "icecast" ? (
          <input type="text" value={settings.mount_point} onChange={(e) => setSettings({ ...settings, mount_point: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
            placeholder="/stream" data-testid="stream-mount-input" />
        ) : (
          <input type="number" value={settings.stream_id} onChange={(e) => setSettings({ ...settings, stream_id: parseInt(e.target.value) || 1 })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
            placeholder="1" data-testid="stream-sid-input" />
        )}
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`rounded-xl p-4 mb-4 ${testResult.online ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`} data-testid="stream-test-result">
          {testResult.online ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wifi className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm font-medium">Connected — {testResult.server_type}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-white/50">
                <span>Listeners: <span className="text-white/80">{testResult.listeners}</span></span>
                <span>Peak: <span className="text-white/80">{testResult.peak_listeners}</span></span>
                <span>Now Playing: <span className="text-white/80">{testResult.now_playing?.artist} - {testResult.now_playing?.title || "N/A"}</span></span>
                {testResult.stream_name && <span>Stream: <span className="text-white/80">{testResult.stream_name}</span></span>}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <WifiOff className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm">{testResult.error || "Connection failed"}</span>
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="flex items-center gap-3">
        <button onClick={handleTest} disabled={testing || !settings.server_url}
          className="bg-white/10 hover:bg-white/15 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition disabled:opacity-30 flex items-center gap-2"
          data-testid="stream-test-btn">
          {testing ? <><Loader2 className="w-4 h-4 animate-spin" /> Testing...</> : "Test Connection"}
        </button>
        <button onClick={handleSave} disabled={saving || !settings.server_url}
          className="bg-purple-500 hover:bg-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition disabled:opacity-30"
          data-testid="stream-save-btn">
          {saving ? "Saving..." : saved ? "Saved!" : "Save"}
        </button>
        {hasSaved && (
          <button onClick={handleRemove}
            className="text-red-400/60 hover:text-red-400 text-xs font-medium transition ml-auto"
            data-testid="stream-remove-btn">Remove</button>
        )}
      </div>
    </div>
  );
};

const GENRE_SUGGESTIONS = ["Hip-Hop", "R&B", "Soul", "Jazz", "Pop", "Rock", "Electronic", "House", "Reggae", "Afrobeats", "Gospel", "Country", "Latin"];

const SubmissionPrefsSection = ({ authToken }) => {
  const [prefs, setPrefs] = useState({ accepted_genres: [], bpm_min: null, bpm_max: null, allow_explicit: true, accepting_submissions: true });
  const [newGenre, setNewGenre] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/submission-targets/preferences`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (res.ok) setPrefs(await res.json());
    } catch (e) { console.error(e); }
  }, [authToken]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const savePrefs = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/submission-targets/preferences`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(prefs),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  const addGenre = (g) => {
    const genre = g.trim();
    if (genre && !prefs.accepted_genres.includes(genre)) {
      setPrefs({ ...prefs, accepted_genres: [...prefs.accepted_genres, genre] });
    }
    setNewGenre("");
  };

  const removeGenre = (g) => setPrefs({ ...prefs, accepted_genres: prefs.accepted_genres.filter(x => x !== g) });

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-5" data-testid="submission-prefs-section">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center"><Target className="w-5 h-5 text-purple-400" /></div>
        <div>
          <h3 className="text-lg font-semibold text-white">Submission Targeting Preferences</h3>
          <p className="text-white/40 text-xs">Control what artists can submit to your station</p>
        </div>
      </div>

      {/* Accepting Toggle */}
      <label className="flex items-center gap-3 cursor-pointer" data-testid="accepting-submissions-toggle">
        <input type="checkbox" checked={prefs.accepting_submissions} onChange={e => setPrefs({ ...prefs, accepting_submissions: e.target.checked })} className="w-4 h-4 accent-purple-500" />
        <span className="text-white text-sm">Accepting submissions</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${prefs.accepting_submissions ? "bg-green-400/10 text-green-400" : "bg-red-400/10 text-red-400"}`}>
          {prefs.accepting_submissions ? "Open" : "Closed"}
        </span>
      </label>

      {/* Accepted Genres */}
      <div>
        <label className="text-white/60 text-sm mb-2 block">Accepted Genres</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {prefs.accepted_genres.map(g => (
            <span key={g} className="bg-purple-500/20 text-purple-300 text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
              {g} <button onClick={() => removeGenre(g)} className="hover:text-red-400 transition"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {prefs.accepted_genres.length === 0 && <span className="text-white/30 text-xs">All genres accepted (no filter)</span>}
        </div>
        <div className="flex gap-2 mb-2">
          <input type="text" value={newGenre} onChange={e => setNewGenre(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addGenre(newGenre))}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition" placeholder="Add genre..."
            data-testid="genre-input" />
          <button onClick={() => addGenre(newGenre)} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-3 py-2 rounded-lg transition"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {GENRE_SUGGESTIONS.filter(g => !prefs.accepted_genres.includes(g)).slice(0, 8).map(g => (
            <button key={g} onClick={() => addGenre(g)} className="text-[11px] text-white/30 hover:text-white/60 bg-white/5 hover:bg-white/10 px-2 py-1 rounded-md transition">{g}</button>
          ))}
        </div>
      </div>

      {/* BPM Range */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-white/60 text-sm mb-1 block">Min BPM</label>
          <input type="number" min="40" max="220" value={prefs.bpm_min || ""} onChange={e => setPrefs({ ...prefs, bpm_min: e.target.value ? parseInt(e.target.value) : null })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition" placeholder="No minimum"
            data-testid="bpm-min-input" />
        </div>
        <div>
          <label className="text-white/60 text-sm mb-1 block">Max BPM</label>
          <input type="number" min="40" max="220" value={prefs.bpm_max || ""} onChange={e => setPrefs({ ...prefs, bpm_max: e.target.value ? parseInt(e.target.value) : null })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500 transition" placeholder="No maximum"
            data-testid="bpm-max-input" />
        </div>
      </div>

      {/* Explicit Toggle */}
      <label className="flex items-center gap-3 cursor-pointer" data-testid="allow-explicit-toggle">
        <input type="checkbox" checked={prefs.allow_explicit} onChange={e => setPrefs({ ...prefs, allow_explicit: e.target.checked })} className="w-4 h-4 accent-purple-500" />
        <span className="text-white text-sm">Allow explicit content</span>
      </label>

      {/* Save */}
      <button onClick={savePrefs} disabled={saving}
        className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white font-medium px-6 py-2.5 rounded-xl transition flex items-center gap-2 text-sm"
        data-testid="save-submission-prefs">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
        {saving ? "Saving..." : saved ? "Saved!" : "Save Preferences"}
      </button>
    </div>
  );
};

export const SettingsTab = ({ currentUser, authToken, dashboardTheme, onThemeChange }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Deletion failed');
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (e) {
      setDeleteError(e.message);
      setDeleting(false);
    }
  };

  const isListener = currentUser?.plan === 'listener' || currentUser?.user_type === 'listener';

  return (
  <div className="space-y-6">
    {/* Dashboard Theme Picker */}
    <ThemePicker currentTheme={dashboardTheme} onThemeChange={onThemeChange} />

    {/* External Stream Connection — only for station owners */}
    {!isListener && <ExternalStreamSection authToken={authToken} />}

    {/* Submission Targeting Preferences — only for station owners */}
    {!isListener && <SubmissionPrefsSection authToken={authToken} />}

    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Profile Settings</h3>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-20 h-20 bg-purple-500 rounded-full flex items-center justify-center overflow-hidden">
          {currentUser.picture ? <img src={currentUser.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-white" />}
        </div>
        <div>
          <p className="text-white font-medium">{currentUser.name}</p>
          <p className="text-white/50 text-sm">{currentUser.email}</p>
          <p className="text-purple-400 text-xs mt-1 capitalize">{currentUser.plan === 'premium' ? 'Premium' : currentUser.plan} Plan</p>
        </div>
      </div>
      <ProfileSettingsForm currentUser={currentUser} authToken={authToken} />
    </div>
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
      <PasswordChangeForm authToken={authToken} />
    </div>
    <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-2">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <h3 className="text-lg font-semibold text-red-400">Danger Zone</h3>
      </div>
      <p className="text-white/50 text-sm mb-4">Once you delete your account, there is no going back. All your stations, followers, and data will be permanently removed.</p>
      {!showDeleteConfirm ? (
        <button onClick={() => setShowDeleteConfirm(true)} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-6 py-2 rounded-xl transition" data-testid="delete-account-btn">Delete Account</button>
      ) : (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-3">
          <p className="text-white/70 text-sm">Type <strong className="text-red-400">DELETE</strong> to confirm:</p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            className="w-full bg-white/5 border border-red-500/30 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-red-500 transition"
            placeholder="Type DELETE"
            data-testid="delete-confirm-input"
          />
          {deleteError && <p className="text-red-400 text-sm">{deleteError}</p>}
          <div className="flex gap-3">
            <button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
              className="bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white px-5 py-2 rounded-lg text-sm font-medium transition"
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete Account'}
            </button>
            <button onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(''); }}
              className="bg-white/10 hover:bg-white/15 text-white/70 px-5 py-2 rounded-lg text-sm transition"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};
